import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourceDetailPage() {
  const { slug } = useParams();
  const [resource, setResource] = useState(null);
  const [relatedTrips, setRelatedTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResource();
  }, [slug]);

  const fetchResource = async () => {
    try {
      const res = await axios.get(`${API}/resources/${slug}`);
      setResource(res.data);
      
      // Fetch related trips based on resource's related_trips
      if (res.data.related_trips && res.data.related_trips.length > 0) {
        const tripsRes = await axios.get(`${API}/trips`);
        const related = tripsRes.data.filter(t => 
          res.data.related_trips.includes(t.slug)
        );
        setRelatedTrips(related);
      }
    } catch (err) {
      console.error('Failed to fetch resource:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Article not found</p>
          <Link to="/resources">
            <Button variant="outline">Back to Resources</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img
          src={resource.image_url}
          alt={resource.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-4">
              {resource.categories.map((cat) => (
                <Badge key={cat} className="bg-secondary text-white border-0">
                  {cat}
                </Badge>
              ))}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {resource.title}
            </h1>
            <div className="flex items-center gap-4 text-white/80">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{new Date(resource.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <span>•</span>
              <span>By Circulate Travel</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/resources" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-8">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Resources
        </Link>

        {/* Article Intro */}
        {resource.excerpt && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-muted/30 rounded-xl border-l-4 border-secondary"
          >
            <p className="text-lg text-muted-foreground leading-relaxed italic">
              {resource.excerpt}
            </p>
          </motion.div>
        )}

        {/* Article Content */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-primary prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3"
        >
          <div 
            className="text-muted-foreground leading-relaxed space-y-6 [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-primary [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:text-xl [&>h3]:font-bold [&>h3]:text-primary [&>h3]:mt-6 [&>h3]:mb-3 [&>h4]:text-lg [&>h4]:font-semibold [&>h4]:text-primary [&>h4]:mt-4 [&>h4]:mb-2"
            dangerouslySetInnerHTML={{ __html: resource.content }}
          />
        </motion.article>

        {/* Related Trips */}
        {relatedTrips.length > 0 && (
          <div className="mt-16 pt-12 border-t">
            <h2 className="font-heading text-2xl font-semibold text-primary mb-6">
              Related Trips
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedTrips.map((trip) => (
                <Link key={trip.id} to={`/trips/${trip.slug}`}>
                  <Card className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all">
                    <div className="flex">
                      <div className="w-1/3 relative">
                        <img
                          src={trip.image_url}
                          alt={trip.title}
                          className="w-full h-full object-cover absolute inset-0"
                        />
                      </div>
                      <CardContent className="w-2/3 p-4">
                        <Badge className="bg-secondary/10 text-secondary border-0 mb-2 text-xs">
                          {trip.trip_type === 'both' ? 'Group & Solo' : trip.trip_type === 'group' ? 'Group' : 'Solo'}
                        </Badge>
                        <h3 className="font-heading font-semibold text-primary group-hover:text-secondary transition-colors">
                          {trip.title}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                          <MapPin className="h-3 w-3" />
                          <span>{trip.destination}</span>
                        </div>
                        <p className="text-secondary font-semibold mt-2">
                          From ${trip.price.toLocaleString()}
                        </p>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-secondary/10 rounded-2xl p-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-primary mb-3">
            Ready to Experience This Destination?
          </h3>
          <p className="text-muted-foreground mb-4">
            Book your adventure today and pay over time with our flexible installment plans.
          </p>
          <Link to="/trips">
            <Button className="bg-secondary hover:bg-secondary/90 text-white rounded-full">
              Explore All Trips
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
